import React, { ReactNode } from 'react';

import { IComponent } from '../../../typings/generic-component';
import { getFooter, getHeader } from '../../../utils/match-header-footer';
import { addIsLoseOnTeams, getMatchResult, isLive, isPreMatch } from '../../../utils/match-status';
import { COMPETITIONLOGO, placeholdersMap, TEAMLOGO } from '../../../utils/placeholders-football';
import { makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Cta from '../../atoms/cta/cta';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import LiveOnSkyBadge from '../../atoms/live-on-sky-badge/live-on-sky-badge';
import OutlinedCard from '../../atoms/outlined-card/outlined-card';
import Text from '../../atoms/text/text';
import MatchCountdown from '../../molecules/match-countdown/match-countdown';
import { getMatchTitle } from '../../molecules/match-data/match-data';
import { IMatchRow } from '../../molecules/match-row/match-row';
import MatchStatusWrapper from '../../molecules/match-status/match-status-wrapper';
import Team from '../../molecules/team/team';

import './team-match-card.scss';

interface ICompetition {
  id: string;
  name: string;
  homepageUrl: string;
  logoUrl: string;
}
export interface ITeamMatchCard extends IMatchRow, IComponent {
  matchDetailUrl?: string | null;
  competition: ICompetition;
  withCompetitionLogo?: boolean;
  countdownEnabled?: boolean;
}

const classNames = (isPreMatch: boolean) =>
  makeClassNames({
    root: ['team-match-card', isPreMatch && 'team-match-card--pre-match'],
    blockWrapper: 'team-match-card__block-wrapper',
    results: [
      'team-match-card__results',
      !isPreMatch && 'team-match-card__results--not-pre-match',
      isPreMatch && 'team-match-card__results--pre-match',
    ],
    resultsFooter: 'team-match-card__results-footer',
    resultsHeader: 'team-match-card__results-header',
    dataWrapper: 'team-match-card__match-data-wrapper',
    liveLabel: 'team-match-card__live-label',
    competitionName: 'team-match-card__competition-name',
    labelDateLong: 'team-match-card__label-datelong',
    labelDateShort: 'team-match-card__label-dateshort',
    logoWrapper: 'team-match-card__logo-wrapper',
    teamName: 'team-match-card__team-name--ellipsis',
    liveOnSky: 'team-match-card__live-on-sky',
  });

const getDate = (title: string, dateLong: string, className: string): ReactNode => (
  <Text className={className} fontSize="16" color="blue">
    {title}
    {title && ' - '}
    {dateLong}
  </Text>
);

const TeamMatchCard = ({
  /* istanbul ignore next: default value */ dataAttributes = {},
  withCompetitionLogo,
  competition,
  matchDetailUrl,
  countdownEnabled,
  ...propMatch
}: ITeamMatchCard) => {
  const match = addIsLoseOnTeams(propMatch);
  const { date, home, away, status, liveOnSky } = match;
  const cn = classNames(isPreMatch(status));

  const title = getMatchTitle(match);
  const dateLong = dateFormat({ date, format: 'ddd D MMM' });
  const dateShort = dateFormat({ date, format: 'ddd D MMM' });
  const header = getHeader(match, true);
  const footer = getFooter(match);
  const result = getMatchResult(match, true);

  return (
    <Cta
      trackingData={dataAttributes}
      narrow
      uppercase={false}
      href={matchDetailUrl ?? ''}
      className={cn.root}
      hasWrapper={false}
    >
      <OutlinedCard
        header={
          <div className={cn.dataWrapper}>
            <Text fontSize="16" fontWeight="500" className={cn.competitionName} color="blue">
              {competition.name}
            </Text>
            {dateLong && getDate(title, dateLong, cn.labelDateLong)}
            {dateShort && getDate(title, dateShort, cn.labelDateShort)}
          </div>
        }
      >
        {withCompetitionLogo && (
          <div className={cn.logoWrapper}>
            <ImgPlaceholder
              src={competition.logoUrl}
              alt={match.customCompetitionName ?? 'logo competizione'}
              placeholder={placeholdersMap[COMPETITIONLOGO]}
            />
          </div>
        )}
        <div className={cn.blockWrapper}>
          {isLive(status) ? (
            <MatchStatusWrapper status={status} backgroundColor className={cn.liveLabel} />
          ) : (
            <div className={cn.resultsHeader}>{header}</div>
          )}
          <Team
            color="blue"
            iconPosition="vertical"
            logoSize="50"
            fontSize="16"
            colorSchema="light"
            ellipsis
            name={home.name}
            logoUrl={home.logoUrl}
            isLose={home.isLose}
          />
          <div className={cn.results}>
            <MatchCountdown matchDate={date} matchStatus={status} enabled={countdownEnabled}>
              {result.text}
            </MatchCountdown>
            <div>
              <div className={cn.resultsFooter}>{footer}</div>
            </div>
          </div>
          <Team
            color="blue"
            iconPosition="vertical"
            logoSize="50"
            fontSize="16"
            colorSchema="light"
            ellipsis
            name={away.name}
            logoUrl={away.logoUrl}
            placeholder={placeholdersMap[TEAMLOGO]}
            isLose={away.isLose}
          />
        </div>
        <div className={cn.liveOnSky}>
          {liveOnSky && (isPreMatch(status) || isLive(status)) && (
            <LiveOnSkyBadge liveOnSky={liveOnSky} fontSize="14" />
          )}
        </div>
      </OutlinedCard>
    </Cta>
  );
};

export default TeamMatchCard;
